// frontend/src/pages/SearchProviders.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import L from 'leaflet';
import { Helmet } from 'react-helmet-async'; // For SEO meta tags
import toast from 'react-hot-toast';
import './Home.css'; // Global styles
import './SearchProviders.css';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_API_URL;

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const fetchProviders = async ({ queryKey }) => {
  const [, query, location, service, hours, category, page] = queryKey;
  const response = await axios.get(
    `${API_URL}/api/search/?query=${query}&location=${location}&service=${service}&hours=${hours}&category=${category}&page=${page}`,
    { withCredentials: true }
  );
  return response.data;
};

const fetchCategories = async () => {
  const response = await axios.get(`${API_URL}/api/categories/`, { withCredentials: true });
  return response.data;
};

const fetchUser = async () => {
  const response = await axios.get(`${API_URL}/api/profile/`, { withCredentials: true });
  return response.data;
};

const haversineDistance = (coords1, coords2) => {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(coords2[0] - coords1[0]);
  const dLon = toRad(coords2[1] - coords1[1]);
  const lat1 = toRad(coords1[0]);
  const lat2 = toRad(coords2[0]);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c).toFixed(2);
};

function SearchProviders() {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [service, setService] = useState('');
  const [hours, setHours] = useState('');
  const [category, setCategory] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // For hamburger menu
  const queryClient = useQueryClient();
  const locationHook = useLocation();
  const navigate = useNavigate();

  const { data: providerData, isLoading: providersLoading } = useQuery({
    queryKey: ['searchProviders', searchQuery, location, service, hours, category, currentPage],
    queryFn: fetchProviders,
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
  });

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: false,
  });

  useEffect(() => {
    queryClient.invalidateQueries(['searchProviders']);
    console.log('Invalidating searchProviders due to mount or navigation');
  }, [queryClient, locationHook.pathname]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          setUserLocation([-1.2921, 36.8219]);
        }
      );
    } else {
      setUserLocation([-1.2921, 36.8219]);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      toast.success('Logged out successfully.');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const providers = providerData?.providers || [];
  const totalPages = providerData?.pages || 1;

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'SErraND',
    url: 'https://www.serrand.org',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://www.serrand.org/search?query={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
    description:
      'Search for trusted service providers in Kenya with SErraND. Find plumbers, electricians, tutors, and more near you.',
  };

  return (
    <div className="search-container bg-gradient">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Search Service Providers in Kenya - SErraND</title>
        <meta
          name="description"
          content="Find trusted service providers in Kenya with SErraND. Search for plumbers, electricians, tutors, and more near your location."
        />
        <meta
          name="keywords"
          content="SErraND, service providers, Kenya, plumbers, electricians, tutors, home services, search professionals"
        />
        <meta name="author" content="Marvin Useki" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.serrand.org/search/" />
        <meta property="og:title" content="Search Service Providers in Kenya - SErraND" />
        <meta
          property="og:description"
          content="Discover reliable service providers in Kenya with SErraND. Search for professionals near you."
        />
        <meta property="og:url" content="https://www.serrand.org/search/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.serrand.org/images/search-providers-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Search Service Providers in Kenya - SErraND" />
        <meta name="twitter:description" content="Find trusted service providers in Kenya with SErraND." />
        <meta name="twitter:image" content="https://www.serrand.org/images/search-providers-image.jpg" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <nav className="navbar">
        <div className="navbar-container">
          <Link to="/" className="logo-container nav-logo">
            <span className="se logo-se">SE</span>
            <span className="middle logo-middle">RRA</span>
            <span className="nd logo-nd">ND</span>
          </Link>

          {/* Hamburger Icon for Mobile */}
          <button
            className="md:hidden hamburger"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          </button>

          {/* Navbar Links for Desktop/Tablet */}
          <div className="hidden md:flex md:gap-4">
            <Link to="/" className="btn btn-primary">Home</Link>
            <Link to="/profile" className="btn">Profile</Link>
            <Link to="/contact" className="btn">Contact</Link>
            <Link to="/search" className="btn">Search Providers</Link>
            {userLoading || isAuthLoading ? (
              <span className="text-gray-400 self-center">Loading...</span>
            ) : user?.username ? (
              <>
                <span className="welcome-msg">Jambo, {user.username}!</span>
                <button onClick={handleLogout} className="btn btn-red">Logout</button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/', { state: { showLogin: true } })}
                  className="btn"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/', { state: { showRegister: true } })}
                  className="btn btn-green"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Overlay */}
          {isMenuOpen && (
            <div className="mobile-menu-overlay" onClick={closeMenu}></div>
          )}

          {/* Mobile Menu */}
          <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
            <button
              className="mobile-menu-close"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <span className="close-icon">×</span>
            </button>
            <div className="mobile-menu-header">
              <Link to="/" className="logo-container nav-logo" onClick={closeMenu}>
                <span className="se logo-se">SE</span>
                <span className="middle logo-middle">RRA</span>
                <span className="nd logo-nd">ND</span>
              </Link>
            </div>
            <Link to="/" className="mobile-menu-item" onClick={closeMenu}>Home</Link>
            <Link to="/profile" className="mobile-menu-item" onClick={closeMenu}>Profile</Link>
            <Link to="/contact" className="mobile-menu-item" onClick={closeMenu}>Contact</Link>
            <Link to="/search" className="mobile-menu-item" onClick={closeMenu}>Search Providers</Link>
            {userLoading || isAuthLoading ? (
              <span className="mobile-menu-item text-gray-400">Loading...</span>
            ) : user?.username ? (
              <>
                <span className="mobile-menu-item welcome-msg">Jambo, {user.username}!</span>
                <button
                  onClick={() => {
                    handleLogout();
                    closeMenu();
                  }}
                  className="mobile-menu-item btn btn-red"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate('/', { state: { showLogin: true } });
                    closeMenu();
                  }}
                  className="mobile-menu-item btn btn-blue"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    navigate('/', { state: { showRegister: true } });
                    closeMenu();
                  }}
                  className="mobile-menu-item btn btn-green"
                >
                  Register
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <section className="hero">
        <h1>Find Your Service Provider 🌟</h1>
        <p className="subtitle">Discover trusted professionals tailored to your needs!</p>
      </section>

      <section className="search-bar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-inputs">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, service, or more..."
              className="input"
              aria-label="Search by name or service"
            />
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Location (e.g., Nairobi)"
              className="input"
              aria-label="Location"
            />
            <input
              type="text"
              value={service}
              onChange={(e) => setService(e.target.value)}
              placeholder="Service (e.g., Plumbing)"
              className="input"
              aria-label="Service type"
            />
            <input
              type="text"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="Business Hours (e.g., 9AM-5PM)"
              className="input"
              aria-label="Business hours"
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input"
              disabled={categoriesLoading}
              aria-label="Service category"
            >
              <option value="">All Categories</option>
              {categoriesLoading ? (
                <option>Loading categories...</option>
              ) : categories && categories.length > 0 ? (
                categories.map((cat) => (
                  <option key={cat.id} value={cat.slug}>
                    {cat.name}
                  </option>
                ))
              ) : (
                <option>No categories available</option>
              )}
            </select>
            <button type="submit" className="btn btn-green">
              Search
            </button>
          </div>
        </form>
      </section>

      <section className="map-container">
        <MapContainer
          center={userLocation || [-1.2921, 36.8219]}
          zoom={10}
          style={{ height: '400px', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          {userLocation && (
            <Marker position={userLocation} icon={userIcon}>
              <Popup>Your Location</Popup>
            </Marker>
          )}
          {providers.map(
            (provider) => {
              if (provider.latitude && provider.longitude) {
                console.log(`Provider ${provider.business_name}: lat=${provider.latitude}, lon=${provider.longitude}`);
                return (
                  <Marker key={provider.id} position={[provider.latitude, provider.longitude]}>
                    <Popup>
                      <b>{provider.business_name}</b><br />
                      {provider.services.length > 0
                        ? provider.services.map(s => s.name).join(', ')
                        : provider.service_provided || 'Services not set'}<br />
                      {provider.location || 'Location not set'}<br />
                      {userLocation && (
                        <span>
                          Distance: {haversineDistance(userLocation, [provider.latitude, provider.longitude])} km<br />
                        </span>
                      )}
                      <Link to={`/provider/${provider.id}`}>View Profile</Link>
                    </Popup>
                  </Marker>
                );
              }
              return null;
            }
          )}
        </MapContainer>
      </section>

      <section className="services">
        {providersLoading ? (
          <p className="text-center">Loading providers...</p>
        ) : (
          <>
            <div className="services-grid">
              {providers.length > 0 ? (
                providers.map((provider) => {
                  const uniqueCategorySlugs = [...new Set(provider.services.map(s => s.category).filter(Boolean))];
                  const displayCategory =
                    provider.services.length > 0 && provider.services[0].category
                      ? uniqueCategorySlugs.length > 1
                        ? (categories?.find(cat => cat.is_general)?.name || 'General Services')
                        : provider.services[0].category_name || 'Not set'
                      : 'Not set';
                  const minCharge =
                    provider.services.length > 0 && provider.services[0].category
                      ? uniqueCategorySlugs.length > 1
                        ? Math.min(
                            ...provider.services.map(s =>
                              s.custom_minimum_charge ||
                              (categories?.find(cat => cat.is_general)?.default_minimum_charge || 300)
                            )
                          )
                        : provider.services[0].custom_minimum_charge ||
                          (categories?.find(cat => cat.slug === provider.services[0].category)?.default_minimum_charge || 300)
                      : 300;
                  return (
                    <div key={provider.id} className="service-card">
                      <h3>{provider.business_name}</h3>
                      <p><strong>Services:</strong> {provider.services.length > 0 ? provider.services.map(s => s.name).join(', ') : provider.service_provided || 'Not set'}</p>
                      <p><strong>Category:</strong> {displayCategory}</p>
                      <p><strong>Minimum Charge:</strong> {minCharge} KES</p>
                      <p><strong>Location:</strong> {provider.location || 'Not set'}</p>
                      {userLocation && provider.latitude && provider.longitude && (
                        <p>
                          <strong>Distance:</strong>{' '}
                          {haversineDistance(userLocation, [provider.latitude, provider.longitude])} km
                        </p>
                      )}
                      <p><strong>Hours:</strong> {provider.business_hours || 'Not set'}</p>
                      <p className="description">{provider.description?.slice(0, 90) || 'No description yet.'}...</p>
                      <div className="btn-container">
                        <Link to={`/provider/${provider.id}`} className="btn btn-blue">View Profile</Link>
                        <Link to={`/provider/${provider.id}/chat`} className="btn btn-green">Chat</Link>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="no-services">No providers match your search.</p>
              )}
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-blue"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-blue"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <footer className="footer-new">
        <div className="logo">
          <span className="se logo-se">SE</span>
          <span className="middle logo-middle">RRA</span>
          <span className="nd logo-nd">ND</span>
        </div>
        <p>© 2025 SErraND | Built with Kenyan hustle & chai ☕</p>
      </footer>
    </div>
  );
}

export default SearchProviders;
